import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { useCallback } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import Dialog from '../../atoms/feedback/Dialog'
import { useDocumentSearchConditionFormDialog } from '../../../hooks/useDialog'
import { FlexColumn, FlexRow } from '../../atoms/layout/Flex'
import FormItem from '../../molecules/inputs/FormItem'
import TextField from '../../atoms/inputs/TextField'
import Button from '../../atoms/inputs/Button'
import { DocumentsSelectors } from '../../../store/documents/selectors'
import Label from '../../atoms/display/Label'
import useConsts from '../../../hooks/useConsts'
import useDay from '../../../hooks/useDay'
import { DocumentSearchParams, DocumentStatus, DocumentType, DocumentTypes } from '../../../types/Document'
import useYup from '../../../hooks/useYup'
import useValidationRule from '../../../hooks/useValidationRule'
import { DocumentsActions } from '../../../store/documents'

type DocumentSearchConditionFormDialogProps = {
  id?: string
}

function PaymentDateView(params: { searchParams: DocumentSearchParams }) {
  const { searchParams } = params
  const { paymentDateFrom, paymentDateTo } = searchParams
  const { formatDate } = useDay()

  if (paymentDateFrom == null && paymentDateTo == null) {
    return <></>
  }
  return (
    <FlexRow align="center" wrap>
      <FlexRow width={120}>
        <Label text="支払期限" variant="subtitle" />
      </FlexRow>
      {paymentDateFrom && <Label text={formatDate(paymentDateFrom)} />}
      <Label text="〜" />
      {paymentDateTo && <Label text={formatDate(paymentDateTo)} />}
    </FlexRow>
  )
}

function ClosingDateView(params: { searchParams: DocumentSearchParams }) {
  const { searchParams } = params
  const { closingDateFrom, closingDateTo } = searchParams
  const { formatDate } = useDay()

  if (closingDateFrom == null && closingDateTo == null) {
    return <></>
  }
  return (
    <FlexRow align="center" wrap>
      <FlexRow width={120}>
        <Label text="締め日" variant="subtitle" />
      </FlexRow>
      {closingDateFrom && <Label text={formatDate(closingDateFrom)} />}
      <Label text="〜" />
      {closingDateTo && <Label text={formatDate(closingDateTo)} />}
    </FlexRow>
  )
}

function AmountView(params: { searchParams: DocumentSearchParams }) {
  const { searchParams } = params
  const { amountMin, amountMax } = searchParams

  if (amountMin == null && amountMax == null) {
    return <></>
  }
  return (
    <FlexRow align="center" wrap>
      <FlexRow width={120}>
        <Label text="金額" variant="subtitle" />
      </FlexRow>
      {amountMin && (
        <FlexRow space={0}>
          <Label text={amountMin} format="amountNoStyle" />
          <Label text="円" />
        </FlexRow>
      )}
      <Label text="〜" />
      {amountMax && (
        <FlexRow space={0}>
          <Label text={amountMax} format="amountNoStyle" />
          <Label text="円" />
        </FlexRow>
      )}
    </FlexRow>
  )
}

type DocumentSearchConditionSaveParams = {
  name: string
}

function documentTypeList(types: DocumentTypes): DocumentType[] {
  const list: DocumentType[] = []
  if (types.type1) {
    list.push(1)
  }
  if (types.type2) {
    list.push(2)
  }
  if (types.type3) {
    list.push(3)
  }
  if (types.type4) {
    list.push(4)
  }
  if (types.type5) {
    list.push(5)
  }
  if (types.type6) {
    list.push(6)
  }
  if (types.type7) {
    list.push(7)
  }
  if (types.type8) {
    list.push(8)
  }
  return list
}

export default function DocumentSearchConditionFormDialog({ id }: DocumentSearchConditionFormDialogProps) {
  const { state, close } = useDocumentSearchConditionFormDialog()
  const searchParams = useSelector(DocumentsSelectors.searchParams)
  const documentSearchConditions = useSelector(DocumentsSelectors.searchConditions)
  const searchCondition = documentSearchConditions.find((item) => item.id === id)
  const params = searchCondition ? searchCondition.params : searchParams
  const { documentTypeName, documentStatusName } = useConsts()
  const dispatch = useDispatch()
  const yup = useYup()
  const rules = useValidationRule()
  const schema = yup.object().shape({
    name: rules.documentSearchConditionName,
  })
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DocumentSearchConditionSaveParams>({
    defaultValues: {
      name: searchCondition?.name ?? '',
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  })
  const handleSave = useCallback(
    (values: DocumentSearchConditionSaveParams) => {
      dispatch(
        DocumentsActions.saveSearchCondition({
          id,
          name: values.name,
          params,
        })
      )
    },
    [dispatch, id, params]
  )
  const documentTypes = documentTypeList(params.documentTypes)
  return (
    <Dialog
      open={state !== undefined}
      onClose={close}
      title="お気に入り検索条件の保存"
      onSubmit={handleSubmit(handleSave)}
      actions={<>{id === undefined ? <Button type="submit">作成</Button> : <Button type="submit">保存</Button>}</>}
    >
      <FlexColumn>
        <FormItem label="タイトル" required>
          <TextField register={register('name')} error={errors.name?.message} />
        </FormItem>
        <Label text="検索条件" variant="title" />
        {params.keyword && (
          <FlexRow align="center" wrap>
            <FlexRow width={120}>
              <Label text="検索キーワード" variant="subtitle" />
            </FlexRow>
            <Label text={params.keyword} />
          </FlexRow>
        )}
        {documentTypes.length > 0 && (
          <FlexRow align="center" wrap>
            <FlexRow width={120}>
              <Label text="書類種別" variant="subtitle" />
            </FlexRow>
            {documentTypes.map((type) => (
              <Label text={documentTypeName(type)} />
            ))}
          </FlexRow>
        )}
        {params.documentStatus !== undefined && (
          <FlexRow align="center" wrap>
            <FlexRow width={120}>
              <Label text="書類の状態" variant="subtitle" />
            </FlexRow>
            {Number(params.documentStatus) === 0 ? (
              <Label text="全て" />
            ) : (
              <Label text={documentStatusName(Number(params.documentStatus) as DocumentStatus)} />
            )}
          </FlexRow>
        )}
        <PaymentDateView searchParams={params} />
        <ClosingDateView searchParams={params} />
        <AmountView searchParams={params} />
        <FlexRow align="center" wrap>
          <FlexRow width={120}>
            <Label text="検索対象" variant="subtitle" />
          </FlexRow>
          {params.searchType === 1 && <Label text="書類を表示" />}
          {params.searchType === 2 && <Label text="項目を表示" />}
        </FlexRow>
      </FlexColumn>
    </Dialog>
  )
}
